// src/components/hotelRatings/HotelRatingsTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetHotelRatings from "../../hooks/hotelRatings/useGetHotelRatings";
import useDeleteHotelRating from "../../hooks/hotelRatings/useDeleteHotelRating";
import useDeleteAllHotelRatings from "../../hooks/hotelRatings/useDeleteAllHotelRatings";
import useRestoreHotelRating from "../../hooks/hotelRatings/useRestoreHotelRating";
import useRestoreAllHotelRatings from "../../hooks/hotelRatings/useRestoreAllHotelRatings";
import useForceDeleteHotelRating from "../../hooks/hotelRatings/useForceDeleteHotelRating";
import useForceDeleteAllHotelRatings from "../../hooks/hotelRatings/useForceDeleteAllHotelRatings";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import HotelRatingsTableRow from "./Table/HotelRatingsTableRow";
import HotelRatingsTableColumns from "./Table/HotelRatingsTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const HotelRatingsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("hotel_ratings_list"),
            create: has("hotel_ratings_create"),
            edit: has("hotel_ratings_edit"),
            delete: has("hotel_ratings_delete"),
            forceDelete: has("hotel_ratings_force_delete"),
            archiveList: has("hotel_ratings_archive_list"),
            restore: has("hotel_ratings_restore"),
            restoreAll: has("hotel_ratings_restore-all-table"),
            deleteAll: has("hotel_ratings_delete-all-table"),
            massForceDeleteAll: has("hotel_ratings_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/hotel-ratings/archive");

    // CRUD Hooks
    const { deleteHotelRatings, isDelete } = useDeleteHotelRating();
    const { deleteAllHotelRatings, isDeleteAll } = useDeleteAllHotelRatings();
    const { restoreHotelRating, isRestore } = useRestoreHotelRating();
    const { restoreAllHotelRatings, isRestoreAll } = useRestoreAllHotelRatings();
    const { forceDeleteHotelRating, isForceDelete } = useForceDeleteHotelRating();
    const { forceDeleteAllHotelRatings, isForceDeleteAll } = useForceDeleteAllHotelRatings();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/hotel-ratings",
        deleteSingle: deleteHotelRatings,
        deleteAll: deleteAllHotelRatings,
        restoreSingle: restoreHotelRating,
        restoreAll: restoreAllHotelRatings,
        forceDeleteSingle: forceDeleteHotelRating,
        forceDeleteAll: forceDeleteAllHotelRatings,
        isArchive,
    });

    // ✨ Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    // ✨ Updated filters to include status and sorting
    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    // ✨ Filter configuration for TableFilterBar
    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    // ✨ Clear all filters handler
    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, hotelRatings, meta } = useGetHotelRatings(
        page,
        perPage,
        filters
    );

    const items = useMemo(() => hotelRatings?.items || [], [hotelRatings]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <HotelRatingsTableRow
            key={params.item.id}
            hotelRating={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive
        ? !perms.massForceDeleteAll
        : !perms.deleteAll;

    const title = isArabic ? "تصنيفات الفنادق" : "Hotel Ratings";
    const subtitle = isArchive
        ? isArabic
            ? "عرض تصنيفات الفنادق المحذوفة (الأرشيف)"
            : "Archived hotel ratings (soft deleted)"
        : isArabic
            ? "إدارة تصنيفات الفنادق المتاحة في النظام"
            : "Manage available hotel ratings in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة تصنيف"
            : "Create new hotel rating";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للاستعادة"
                        : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllHotelRatings({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للإنشاء"
                        : "No create permission"
                );
                return;
            }

            navigate("/hotel-ratings/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/hotel-ratings/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/hotel-ratings"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={HotelRatingsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={
                    isArchive ? "لا توجد تصنيفات في الأرشيف" : "لا توجد تصنيفات فنادق"
                }
                emptyLabelEn={isArchive ? "No archived hotel ratings" : "No hotel ratings"}
                loadErrorAr="حدث خطأ أثناء تحميل تصنيفات الفنادق"
                loadErrorEn="Failed to load hotel ratings"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default HotelRatingsTable;
